// src/features/vertraege/components/VertragPanel.tsx
//
// Vertragsbereich im Kundenformular. Zeigt die Vertragshistorie und
// erzeugt neue Entwuerfe aus der aktiven Vorlage.
//
// Der Entwurf wird serverseitig gerendert. Beim Oeffnen des Formulars
// laedt die Komponente zuerst eine reine Vorschau ohne Schreibvorgang.
// Deren Antwort liefert zugleich die aus den Stammdaten abgeleiteten
// Werte, mit denen das Formular vorbelegt wird. Fehlen Stammdaten,
// meldet dieselbe Antwort genau, welche.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  FileSignature,
  Plus,
  Eye,
  Trash2,
  Download,
  AlertCircle,
  RefreshCw,
  X
} from 'lucide-react';
import { vertragService } from '../services/VertragService';
import {
  Vertrag,
  VertragVariablen,
  Vertretungsberechtigter,
  VERTRAG_STATUS_LABEL,
  VERTRAG_STATUS_FARBE
} from '../types';

interface VertragPanelProps {
  customerId: string;
}

// Felder, die im Formular bearbeitbar sind. Alles andere kommt aus den
// Standardwerten der Vorlage und wird hier nicht angefasst.
const FELDER: Array<{ schluessel: keyof VertragVariablen; label: string; hinweis?: string; optional?: boolean }> = [
  { schluessel: 'kunde_vertreter_bezeichnung', label: 'Vertreten durch', hinweis: 'Erscheint im Vertragskopf vor dem Namen, zum Beispiel "ihren Geschäftsführer".' },
  { schluessel: 'start_abrechnungsmonat', label: 'Erster Abrechnungsmonat', hinweis: 'Aus dem Beginn der Zusammenarbeit abgeleitet.' },
  { schluessel: 'preis_pro_arbeitnehmer', label: 'Preis pro Arbeitnehmer', hinweis: 'Aus der Preispflege abgeleitet.' },
  { schluessel: 'onboarding_pauschale', label: 'Onboarding-Pauschale', hinweis: 'Leer lassen, wenn keine vereinbart ist.', optional: true },
  { schluessel: 'onboarding_monat', label: 'Monat der Onboarding-Pauschale', optional: true },
  { schluessel: 'haftung_hoechstbetrag', label: 'Haftungshöchstbetrag' }
];

const labelStil: React.CSSProperties = {
  fontSize: '14px',
  fontWeight: 500,
  color: '#374151'
};

const inputStil: React.CSSProperties = {
  padding: '8px 12px',
  border: '1px solid #D1D5DB',
  borderRadius: '6px',
  fontSize: '14px',
  color: '#111827',
  backgroundColor: 'white',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box'
};

const hinweisStil: React.CSSProperties = {
  fontSize: '12px',
  color: '#6B7280',
  margin: 0
};

function formatDatumZeit(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
}

export const VertragPanel: React.FC<VertragPanelProps> = ({ customerId }) => {
  const [vertraege, setVertraege] = useState<Vertrag[]>([]);
  const [kontakte, setKontakte] = useState<Vertretungsberechtigter[]>([]);
  const [laden, setLaden] = useState(true);

  const [formOffen, setFormOffen] = useState(false);
  const [bearbeiteterVertrag, setBearbeiteterVertrag] = useState<string | null>(null);
  const [empfaengerId, setEmpfaengerId] = useState<string>('');
  const [variablen, setVariablen] = useState<VertragVariablen>({});

  const [arbeitet, setArbeitet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [fehlendeDaten, setFehlendeDaten] = useState<string[]>([]);
  const [hinweis, setHinweis] = useState<string | null>(null);

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfName, setPdfName] = useState<string>('Vertrag.pdf');
  const pdfUrlRef = useRef<string | null>(null);

  const setzePdf = useCallback((base64: string | null, name: string) => {
    if (pdfUrlRef.current) {
      URL.revokeObjectURL(pdfUrlRef.current);
      pdfUrlRef.current = null;
    }
    if (!base64) {
      setPdfUrl(null);
      return;
    }
    const url = vertragService.base64ZuBlobUrl(base64);
    pdfUrlRef.current = url;
    setPdfUrl(url);
    setPdfName(name);
  }, []);

  useEffect(() => {
    return () => {
      if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current);
    };
  }, []);

  const neuLaden = useCallback(async () => {
    setLaden(true);
    const [liste, personen] = await Promise.all([
      vertragService.getVertraege(customerId),
      vertragService.getVertretungsberechtigte(customerId)
    ]);
    setVertraege(liste);
    setKontakte(personen);
    setLaden(false);
  }, [customerId]);

  useEffect(() => {
    neuLaden();
  }, [neuLaden]);

  const fehlerUebernehmen = (antwort: { error: string; fehlendeDaten?: string[]; auswahl?: Array<{ id: string; name: string; funktion: string | null }> }) => {
    setFehler(antwort.error);
    setFehlendeDaten(antwort.fehlendeDaten || []);
    if (antwort.auswahl && antwort.auswahl.length > 0) {
      setKontakte(antwort.auswahl.map((a) => ({
        id: a.id,
        vorname: a.name.split(' ')[0] || null,
        nachname: a.name.split(' ').slice(1).join(' ') || null,
        email: '',
        geschlecht: null,
        funktion: a.funktion
      })));
    }
  };

  /** Formular oeffnen und mit den abgeleiteten Werten vorbelegen. */
  const formularOeffnen = async () => {
    setFormOffen(true);
    setBearbeiteterVertrag(null);
    setFehler(null);
    setFehlendeDaten([]);
    setHinweis(null);
    setzePdf(null, 'Vertrag.pdf');
    setArbeitet(true);

    const vorauswahl = kontakte.length === 1 ? kontakte[0].id : '';
    setEmpfaengerId(vorauswahl);

    const antwort = await vertragService.erzeugen({
      customerId,
      empfaengerContactId: vorauswahl || undefined,
      previewOnly: true
    });

    if (antwort.success) {
      setVariablen(antwort.variablen);
      setzePdf(antwort.pdfBase64, antwort.filename);
    } else {
      fehlerUebernehmen(antwort);
    }
    setArbeitet(false);
  };

  const vorschauAktualisieren = async () => {
    setArbeitet(true);
    setFehler(null);
    setFehlendeDaten([]);
    setHinweis(null);

    const antwort = await vertragService.erzeugen({
      customerId,
      empfaengerContactId: empfaengerId || undefined,
      variablen,
      previewOnly: true
    });

    if (antwort.success) {
      setVariablen(antwort.variablen);
      setzePdf(antwort.pdfBase64, antwort.filename);
    } else {
      setzePdf(null, 'Vertrag.pdf');
      fehlerUebernehmen(antwort);
    }
    setArbeitet(false);
  };

  const entwurfSpeichern = async () => {
    setArbeitet(true);
    setFehler(null);
    setFehlendeDaten([]);
    setHinweis(null);

    const antwort = await vertragService.erzeugen({
      customerId,
      vertragId: bearbeiteterVertrag || undefined,
      empfaengerContactId: empfaengerId || undefined,
      variablen
    });

    if (antwort.success) {
      setzePdf(antwort.pdfBase64, antwort.filename);
      setBearbeiteterVertrag(antwort.vertrag ? antwort.vertrag.id : null);
      setHinweis(antwort.vertrag ? `Entwurf ${antwort.vertrag.vertragsnummer} gespeichert.` : 'Entwurf gespeichert.');
      await neuLaden();
    } else {
      fehlerUebernehmen(antwort);
    }
    setArbeitet(false);
  };

  /** Bestehenden Vertrag mit seinen eingefrorenen Werten erneut rendern. */
  const vertragAnsehen = async (vertrag: Vertrag) => {
    setArbeitet(true);
    setFehler(null);
    setFehlendeDaten([]);
    setHinweis(null);
    setFormOffen(false);

    const antwort = await vertragService.erzeugen({
      customerId,
      empfaengerContactId: vertrag.empfaenger_contact_id || undefined,
      variablen: vertrag.variablen,
      previewOnly: true
    });

    if (antwort.success) {
      setzePdf(antwort.pdfBase64, vertrag.pdf_dateiname || antwort.filename);
    } else {
      fehlerUebernehmen(antwort);
    }
    setArbeitet(false);
  };

  const entwurfBearbeiten = (vertrag: Vertrag) => {
    setFormOffen(true);
    setBearbeiteterVertrag(vertrag.id);
    setEmpfaengerId(vertrag.empfaenger_contact_id || '');
    setVariablen(vertrag.variablen || {});
    setFehler(null);
    setFehlendeDaten([]);
    setHinweis(null);
  };

  const entwurfLoeschen = async (vertrag: Vertrag) => {
    const ergebnis = await vertragService.entwurfLoeschen(vertrag.id);
    if (!ergebnis.success) {
      setFehler(ergebnis.error || 'Löschen fehlgeschlagen.');
      return;
    }
    if (bearbeiteterVertrag === vertrag.id) {
      setFormOffen(false);
      setBearbeiteterVertrag(null);
      setzePdf(null, 'Vertrag.pdf');
    }
    await neuLaden();
  };

  const feldAendern = (schluessel: string, wert: string) => {
    setVariablen((vorher) => ({ ...vorher, [schluessel]: wert }));
  };

  return (
    <div style={{ marginTop: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FileSignature size={16} color="#374151" />
          <h4 style={{ fontSize: '15px', fontWeight: 600, color: '#374151', margin: 0 }}>
            Digitale Verträge
          </h4>
        </div>
        {!formOffen && (
          <button
            type="button"
            onClick={formularOeffnen}
            disabled={arbeitet}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '8px 14px', backgroundColor: arbeitet ? '#9CA3AF' : '#3465DE',
              color: 'white', border: 'none', borderRadius: '6px',
              fontSize: '14px', fontWeight: 500, cursor: arbeitet ? 'not-allowed' : 'pointer'
            }}
          >
            <Plus size={15} />
            Vertrag erzeugen
          </button>
        )}
      </div>

      {/* Vertragshistorie */}
      {laden ? (
        <p style={{ ...hinweisStil, margin: '8px 0' }}>Wird geladen...</p>
      ) : vertraege.length === 0 ? (
        <p style={{ ...hinweisStil, margin: '8px 0' }}>
          Für diesen Kunden wurde noch kein Vertrag erzeugt.
        </p>
      ) : (
        <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden' }}>
          {vertraege.map((vertrag, index) => {
            const farbe = VERTRAG_STATUS_FARBE[vertrag.status];
            return (
              <div
                key={vertrag.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  padding: '10px 14px',
                  borderTop: index === 0 ? 'none' : '1px solid #F3F4F6',
                  backgroundColor: 'white'
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>
                      {vertrag.vertragsnummer}
                    </span>
                    <span style={{
                      fontSize: '11px', fontWeight: 500,
                      color: farbe.text, backgroundColor: farbe.hintergrund,
                      border: `1px solid ${farbe.rand}`,
                      padding: '2px 8px', borderRadius: '4px'
                    }}>
                      {VERTRAG_STATUS_LABEL[vertrag.status]}
                    </span>
                  </div>
                  <p style={{ ...hinweisStil, marginTop: '3px' }}>
                    {formatDatumZeit(vertrag.created_at)}
                    {vertrag.empfaenger_name ? ` · ${vertrag.empfaenger_name}` : ''}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => vertragAnsehen(vertrag)}
                  disabled={arbeitet}
                  title="Ansehen"
                  style={{
                    display: 'flex', alignItems: 'center', gap: '5px',
                    padding: '6px 10px', backgroundColor: 'white',
                    border: '1px solid #D1D5DB', borderRadius: '6px',
                    fontSize: '13px', color: '#374151',
                    cursor: arbeitet ? 'not-allowed' : 'pointer'
                  }}
                >
                  <Eye size={14} />
                  Ansehen
                </button>

                {vertrag.status === 'entwurf' && (
                  <>
                    <button
                      type="button"
                      onClick={() => entwurfBearbeiten(vertrag)}
                      style={{
                        padding: '6px 10px', backgroundColor: 'white',
                        border: '1px solid #D1D5DB', borderRadius: '6px',
                        fontSize: '13px', color: '#374151', cursor: 'pointer'
                      }}
                    >
                      Bearbeiten
                    </button>
                    <button
                      type="button"
                      onClick={() => entwurfLoeschen(vertrag)}
                      title="Entwurf löschen"
                      style={{
                        display: 'flex', alignItems: 'center',
                        padding: '6px 8px', backgroundColor: 'white',
                        border: '1px solid #FECACA', borderRadius: '6px',
                        color: '#DC2626', cursor: 'pointer'
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Formular */}
      {formOffen && (
        <div style={{
          marginTop: '16px', padding: '16px',
          border: '1px solid #E5E7EB', borderRadius: '8px', backgroundColor: '#FAFAFA'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
            <span style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>
              {bearbeiteterVertrag ? 'Entwurf bearbeiten' : 'Neuer Vertragsentwurf'}
            </span>
            <button
              type="button"
              onClick={() => { setFormOffen(false); setBearbeiteterVertrag(null); }}
              style={{ display: 'flex', background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', padding: 0 }}
            >
              <X size={18} />
            </button>
          </div>

          {kontakte.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' }}>
              <label style={labelStil}>Unterzeichnet für den Kunden</label>
              <select
                value={empfaengerId}
                onChange={(e) => setEmpfaengerId(e.target.value)}
                style={inputStil}
              >
                <option value="">Bitte wählen</option>
                {kontakte.map((k) => (
                  <option key={k.id} value={k.id}>
                    {`${k.vorname || ''} ${k.nachname || ''}`.trim()}
                    {k.funktion ? ` (${k.funktion})` : ''}
                  </option>
                ))}
              </select>
              <p style={hinweisStil}>
                Nur aktive Ansprechpartner, die als vertretungsberechtigt gekennzeichnet sind.
              </p>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
            {FELDER.map((feld) => (
              <div key={String(feld.schluessel)} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={labelStil}>
                  {feld.label}
                  {feld.optional && <span style={{ color: '#9CA3AF', fontWeight: 400 }}> (optional)</span>}
                </label>
                <input
                  type="text"
                  value={variablen[feld.schluessel as string] || ''}
                  onChange={(e) => feldAendern(String(feld.schluessel), e.target.value)}
                  style={inputStil}
                />
                {feld.hinweis && <p style={hinweisStil}>{feld.hinweis}</p>}
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
            <button
              type="button"
              onClick={vorschauAktualisieren}
              disabled={arbeitet}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '9px 16px', backgroundColor: 'white',
                border: '1px solid #D1D5DB', borderRadius: '6px',
                fontSize: '14px', color: '#374151',
                cursor: arbeitet ? 'not-allowed' : 'pointer'
              }}
            >
              <RefreshCw size={15} />
              Vorschau aktualisieren
            </button>
            <button
              type="button"
              onClick={entwurfSpeichern}
              disabled={arbeitet}
              style={{
                padding: '9px 16px',
                backgroundColor: arbeitet ? '#9CA3AF' : '#3465DE',
                color: 'white', border: 'none', borderRadius: '6px',
                fontSize: '14px', fontWeight: 500,
                cursor: arbeitet ? 'not-allowed' : 'pointer'
              }}
            >
              Als Entwurf speichern
            </button>
          </div>
        </div>
      )}

      {/* Fehlende Stammdaten */}
      {fehler && (
        <div style={{
          marginTop: '14px', padding: '12px 14px',
          backgroundColor: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px',
          display: 'flex', gap: '10px', alignItems: 'flex-start'
        }}>
          <AlertCircle size={17} color="#DC2626" style={{ flexShrink: 0, marginTop: '1px' }} />
          <div>
            <p style={{ margin: 0, fontSize: '14px', color: '#991B1B' }}>{fehler}</p>
            {fehlendeDaten.length > 0 && (
              <ul style={{ margin: '6px 0 0 0', paddingLeft: '18px', fontSize: '13px', color: '#991B1B' }}>
                {fehlendeDaten.map((eintrag) => <li key={eintrag}>{eintrag}</li>)}
              </ul>
            )}
          </div>
        </div>
      )}

      {hinweis && (
        <div style={{
          marginTop: '14px', padding: '10px 14px',
          backgroundColor: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: '8px'
        }}>
          <p style={{ margin: 0, fontSize: '14px', color: '#065F46' }}>{hinweis}</p>
        </div>
      )}

      {/* PDF-Vorschau */}
      {pdfUrl && (
        <div style={{ marginTop: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ fontSize: '13px', color: '#6B7280' }}>Vorschau</span>
            <a
              href={pdfUrl}
              download={pdfName}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '6px 12px', backgroundColor: 'white',
                border: '1px solid #D1D5DB', borderRadius: '6px',
                fontSize: '13px', color: '#374151', textDecoration: 'none'
              }}
            >
              <Download size={14} />
              Herunterladen
            </a>
          </div>
          <iframe
            title="Vertragsvorschau"
            src={pdfUrl}
            style={{ width: '100%', height: '620px', border: '1px solid #E5E7EB', borderRadius: '8px' }}
          />
        </div>
      )}
    </div>
  );
};
